import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Play, X, Youtube } from "lucide-react";
import { useSiteContent } from "../context/SiteContentContext";
import { YOUTUBE_CHANNEL } from "../data/content";
import { getYoutubeId, isDirectVideo, youtubeEmbed, youtubeThumb } from "../lib/videoUtils";

const PREVIEW_SECONDS = 5;

export function VideoPlayerModal({ video, onClose }) {
  const youtubeId = getYoutubeId(video?.src || "");

  useEffect(() => {
    if (!video) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
    };
  }, [video, onClose]);

  if (!video) return null;

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/80 grid place-items-center px-4 py-8"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={video.title}
    >
      <div
        className="relative w-full max-w-4xl rounded-3xl overflow-hidden bg-black shadow-hover"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-4 px-5 py-4 bg-[#0A0A0A]">
          <p className="font-display text-white text-xl truncate">{video.title}</p>
          <button
            type="button"
            onClick={onClose}
            className="w-10 h-10 grid place-items-center rounded-full bg-white/10 text-white hover:bg-white/20"
            aria-label="Close video"
          >
            <X size={18} />
          </button>
        </div>
        {youtubeId ? (
          <div className="aspect-video bg-black">
            <iframe
              title={video.title}
              src={youtubeEmbed(youtubeId)}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        ) : (
          <video
            key={video.src}
            src={video.src}
            controls
            autoPlay
            playsInline
            className="w-full max-h-[75vh] bg-black"
          />
        )}
      </div>
    </div>
  );
}

export function VideoPreviewCard({ video, onWatch, paused = false, className = "" }) {
  const ref = useRef(null);
  const youtubeId = getYoutubeId(video.src);
  const canPreviewFile = isDirectVideo(video.src) || (!youtubeId && Boolean(video.src));

  useEffect(() => {
    const el = ref.current;
    if (!el || youtubeId) return undefined;

    const loopPreview = () => {
      if (el.currentTime >= PREVIEW_SECONDS) el.currentTime = 0;
    };
    const onEnded = () => {
      el.currentTime = 0;
      if (!paused) el.play().catch(() => {});
    };
    el.addEventListener("timeupdate", loopPreview);
    el.addEventListener("ended", onEnded);

    const io = new IntersectionObserver(
      ([entry]) => {
        if (paused) {
          el.pause();
          return;
        }
        if (entry.isIntersecting) el.play().catch(() => {});
        else el.pause();
      },
      { threshold: 0.4 }
    );
    io.observe(el);

    if (paused) el.pause();
    else el.play().catch(() => {});

    return () => {
      el.removeEventListener("timeupdate", loopPreview);
      el.removeEventListener("ended", onEnded);
      io.disconnect();
    };
  }, [paused, video.src, youtubeId]);

  if (!video.src) return null;

  return (
    <button
      type="button"
      onClick={() => onWatch(video)}
      data-testid={`video-card-${video.id}`}
      className={`group text-left rounded-3xl overflow-hidden bg-[#0A0A0A] text-white hover:shadow-hover transition-all hover:-translate-y-1 ${className}`}
    >
      <div className="relative aspect-[16/9] overflow-hidden bg-black">
        {youtubeId ? (
          <img src={youtubeThumb(youtubeId)} alt="" className="h-full w-full object-cover" />
        ) : canPreviewFile ? (
          <video
            ref={ref}
            src={video.src}
            muted
            playsInline
            preload="metadata"
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="h-full w-full bg-[#1A1A1A]" />
        )}
        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors" />
        <span className="absolute inset-0 grid place-items-center">
          <span className="w-14 h-14 rounded-full bg-[#EC4899] text-white grid place-items-center shadow-pink group-hover:scale-105 transition-transform">
            <Play size={22} fill="currentColor" className="ml-0.5" />
          </span>
        </span>
        <span className="absolute bottom-3 left-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white/90 bg-black/45 px-3 py-1 rounded-full">
          {youtubeId ? "YouTube" : "Autoplay preview"}
        </span>
      </div>
      <div className="p-6">
        <h3 className="font-display text-2xl leading-tight">{video.title}</h3>
        {video.caption && <p className="mt-2 text-sm text-white/70 leading-relaxed">{video.caption}</p>}
        <p className="mt-4 inline-flex items-center gap-2 text-sm text-[#EC4899]">
          Watch video <ArrowUpRight size={16} />
        </p>
      </div>
    </button>
  );
}

export function ProjectVideosSection() {
  const { videos } = useSiteContent();
  const [active, setActive] = useState(null);
  const items = (videos?.items || []).filter((item) => item.showOnServices !== false && item.src);
  const youtube = videos?.youtubeChannel || YOUTUBE_CHANNEL;

  if (!items.length && !youtube) return null;

  return (
    <section id="videos" className="py-16 md:py-24 scroll-mt-24" data-testid="project-videos">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <p className="font-mono uppercase text-xs tracking-[0.25em] text-[#EC4899]">Past projects</p>
            <h2 className="font-display text-4xl md:text-5xl text-black mt-3 leading-tight">See the work in motion.</h2>
            <p className="mt-4 max-w-xl text-black/65 leading-relaxed">
              Short films from recent Creative Flare sessions. Previews play automatically — tap a card to watch the full video.
            </p>
          </div>
          {youtube && (
            <a
              href={youtube}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="watch-more-youtube"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#EC4899] text-white font-medium hover:bg-[#DB2777] transition-all hover:-translate-y-[2px] shadow-pink"
            >
              <Youtube size={18} />
              Watch more on YouTube
              <ArrowUpRight size={18} />
            </a>
          )}
        </div>

        {items.length > 0 && (
          <div className="grid md:grid-cols-2 gap-5">
            {items.map((video) => (
              <VideoPreviewCard
                key={video.id}
                video={video}
                onWatch={setActive}
                paused={Boolean(active)}
              />
            ))}
          </div>
        )}
      </div>
      <VideoPlayerModal video={active} onClose={() => setActive(null)} />
    </section>
  );
}

export function HomeVideoTeaser() {
  const { videos } = useSiteContent();
  const items = videos?.items || [];
  const video = items.find((item) => item.showOnHome && item.src) || items.find((item) => item.src);
  const [open, setOpen] = useState(false);

  if (!video) return null;

  return (
    <section className="py-16 md:py-24" data-testid="home-video">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-7">
            <VideoPreviewCard video={video} onWatch={() => setOpen(true)} paused={open} />
          </div>
          <div className="lg:col-span-5">
            <p className="font-mono uppercase text-xs tracking-[0.25em] text-[#EC4899]">Past projects</p>
            <h2 className="font-display text-4xl md:text-5xl text-black mt-4 leading-[0.95]">
              A snapshot of <span className="italic text-[#EC4899]">what we do.</span>
            </h2>
            <p className="mt-5 text-lg text-black/70 leading-relaxed">
              Watch a short film from a recent Creative Flare project. See more videos — and the full story behind our programmes — on the services page.
            </p>
            <Link
              to="/services#videos"
              data-testid="home-video-more"
              className="mt-8 inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#EC4899] text-white font-medium hover:bg-[#DB2777] transition-all hover:-translate-y-[2px] shadow-pink"
            >
              See more videos <ArrowUpRight size={18} />
            </Link>
          </div>
        </div>
      </div>
      <VideoPlayerModal video={open ? video : null} onClose={() => setOpen(false)} />
    </section>
  );
}
